/**
 * ConfirmDialog — single-action confirmation modal.
 *
 * Used for any destructive / irreversible admin action. Tiny on purpose
 * (no `<dialog>` polyfill, no portal library) — renders inline when
 * `open=true`, traps focus via `useEffect`, closes on Esc + backdrop.
 */
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'

type Variant = 'primary' | 'danger'

interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: string
  confirmLabel: string
  cancelLabel?: string
  variant?: Variant
  loading?: boolean
  onConfirm(): void
  onCancel(): void
}

export function ConfirmDialog({
  open, title, description, confirmLabel, cancelLabel,
  variant = 'primary', loading = false,
  onConfirm, onCancel,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common')
  const confirmRef = useRef<HTMLButtonElement>(null)

  // Esc to close + focus confirm button on open
  useEffect(() => {
    if (!open) return
    confirmRef.current?.focus()
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onCancel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onCancel])

  if (!open) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-title"
      className={cn(
        'fixed inset-0 z-40 flex items-center justify-center',
        'bg-ink/70 backdrop-blur-sm px-4',
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel()
      }}
    >
      <div className="bg-surface border border-line rounded-md shadow-soft w-full max-w-md p-6">
        <h2 id="confirm-title" className="text-lg font-semibold tracking-tight">
          {title}
        </h2>
        {description && (
          <p className="mt-2 text-sm text-textDim leading-relaxed">
            {description}
          </p>
        )}
        <div className="mt-6 flex items-center justify-end gap-2">
          <Button variant="ghost" size="md" onClick={onCancel} disabled={loading}>
            {cancelLabel ?? t('actions.cancel')}
          </Button>
          <Button
            ref={confirmRef}
            variant={variant === 'danger' ? 'danger' : 'primary'}
            size="md"
            onClick={onConfirm}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  )
}
